<template>
  <el-button type="text" style="padding-bottom: 0; margin: 0 auto" @click="handlePrintImage">打印</el-button>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import dayjs from 'dayjs';
import VueQrcode from '@chenfengyuan/vue-qrcode';

@Component({
  components: { VueQrcode }
})
export default class PrintProductDesignImage extends Vue {
  @Prop({ required: true }) attachment;
  @Prop({ required: true }) productDesign;
  @Prop({ required: true }) currentUser;

  qrCodeDataUrl = '';

  async handlePrintImage() {
    // Generate QR code first
    await this.prepareQRCode(`${this.$root.user.host}/pages/product-designs-qrcode/detail?id=${this.productDesign.id}`);

    // Notify backend about the print action
    this.$request.post(`/product_designs/${this.productDesign.id}/print_image`, {
      signed_id: this.attachment.signed_id
    });

    // Get the image data
    const res = await this.$autoLoading(this.$request.get(
      this.attachment.url,
      null,
      { responseType: 'arraybuffer' }
    ));

    const blob = new Blob([res], { type: this.attachment.content_type });
    const imageUrl = URL.createObjectURL(blob);

    // Create iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';

    // Handle cleanup when focus returns to main window
    const onFocus = () => {
      iframe.onload = null;
      iframe.remove();
      URL.revokeObjectURL(imageUrl);
      window.removeEventListener('focus', onFocus);
    };

    window.addEventListener('focus', onFocus);

    iframe.onload = () => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      // Add print styles
      const style = iframeDoc.createElement('style');
      style.innerHTML = `
        @page { size: auto; margin: 0; }
        body { width: 210mm; height: 297mm; padding: 0; margin: 0; }
        p { margin: 0; }
        table, th, td { border: 1px solid rgb(204, 204, 204); border-collapse: collapse; }
        .qrcode-container { text-align: center; }
        .qrcode-container img { width: 80px; height: 80px; }
      `;
      iframeDoc.getElementsByTagName('head')[0].appendChild(style);

      // Create image element
      const img = iframeDoc.createElement('img');

      // Create text information box
      const textBox = iframeDoc.createElement('div');
      textBox.innerHTML = this.generatePrintContent();

      // Set image properties
      img.src = imageUrl;
      Object.assign(img.style, {
        objectFit: 'contain',
        objectPosition: 'center',
        width: '100%',
        maxHeight: '920px',
        marginTop: '20px'
      });

      // Add elements to iframe document
      iframeDoc.body.appendChild(img);
      iframeDoc.body.appendChild(textBox);

      // Trigger print after a short delay to ensure content is loaded
      setTimeout(() => {
        iframe.contentWindow.print();
      }, 1000);
    };

    document.body.appendChild(iframe);
  }

  async prepareQRCode(url) {
    return new Promise(resolve => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 100;

      // Create a temporary container for the QR code
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.display = 'none';
      document.body.appendChild(tempContainer);

      // Create and mount the QR code component
      const QRCodeComponent = new Vue({
        render: h => h(VueQrcode, {
          props: {
            value: url,
            options: { width: 100 },
            tag: 'svg'
          }
        })
      }).$mount(tempContainer);

      // Wait for the component to render
      setTimeout(() => {
        try {
          // Get the SVG element
          const svgElement = QRCodeComponent.$el;

          // Convert SVG to data URL
          const svgData = new XMLSerializer().serializeToString(svgElement);
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });

          this.qrCodeDataUrl = URL.createObjectURL(svgBlob);

          // Clean up
          QRCodeComponent.$destroy();

          // Make sure we remove the element from the DOM
          // First unmount the component from the container
          QRCodeComponent.$el.parentNode.removeChild(QRCodeComponent.$el);

          // Then remove the container from the document
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
        } catch (error) {
          // Ensure cleanup even on error
          if (QRCodeComponent && QRCodeComponent.$el && QRCodeComponent.$el.parentNode) {
            QRCodeComponent.$el.parentNode.removeChild(QRCodeComponent.$el);
          }
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
        } finally {
          resolve();
        }
      }, 100);
    });
  }

  generatePrintContent() {
    const row = this.productDesign;

    return `
      <div style="margin: 10px 25px 0; display: flex; gap: 50px; font-size: 10px;">
        <div style="flex-shrink: 0; width: 40%; word-break: break-all;">
          <p>版房总监审核时间: ${row.audited_at ? dayjs(row.audited_at).format('YYYY-MM-DD') : '/'}</p>
          <p>要求出版日期: ${row.publication_on ? dayjs(row.publication_on).format('YYYY-MM-DD') : '/'}</p>
          <p>制版师: ${this.$get(row, 'pattern_maker.profile.name') ? row.pattern_maker.profile.name : '/'}</p>
          <p style="color: #FF0000;">备注: ${row.remark ? row.remark : '/'}</p>
          <p>版型: ${row.pattern_type ? row.pattern_type : '/'}</p>
        </div>
        <div style="width: 60%;">
          <table style="width: 100%; font-size: 10px;">
            <tr>
              <th>颜色</th>
              <th>尺码</th>
              <th>数量</th>
            </tr>
            ${row.product_design_item_table_attributes.map(item =>
              `<tr>
                <td style="text-align: center;">${item.color}</td>
                <td style="text-align: center;">${item.size_option_value.name}</td>
                <td style="text-align: center;">${item.quantity}</td>
              </tr>`
            ).join('')}
          </table>
        </div>
        <div class="qrcode-container">
          <img src="${this.qrCodeDataUrl}" alt="QR Code" />
        </div>
      </div>

      <footer style="position: fixed; bottom: 30px; left: 25px; right: 25px; display: flex; justify-content: space-between; font-size: 10px;">
        <p>打印人员: ${this.currentUser.profile.name}</p>
        <p>打印时间: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}</p>
      </footer>
    `;
  }
}
</script>
